import { useUser } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { buildDisplayName } from '@/lib/user-utils';

interface UserProfile {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  email: string | null;
  bio: string | null;
  summary: string | null;
  headline: string | null;
  current_company: string | null;
  current_position: string | null;
  job_title: string | null;
  location: string | null;
  phone: string | null;
  website: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  twitter_url: string | null;
  user_type: string | null;
  account_status: string | null;
  department: string | null;
}

export const useUserAvatar = () => {
  const { user } = useUser();

  const { data: profile } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return null;
      }
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id as any)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }

      return (data as any) as UserProfile;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes after unmount
  });

  const getDisplayName = () => {
    return buildDisplayName({
      first_name: profile?.first_name,
      last_name: profile?.last_name,
      email: profile?.email || user?.email,
    });
  };

  const getFullName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return getDisplayName();
  };

  return {
    profile,
    user,
    displayName: getDisplayName(),
    fullName: getFullName(),
    avatarUrl: profile?.avatar_url,
    email: profile?.email || user?.email,
    firstName: profile?.first_name,
    lastName: profile?.last_name,
    bio: profile?.bio,
    summary: profile?.summary,
    headline: profile?.headline,
    currentCompany: profile?.current_company,
    currentPosition: profile?.current_position,
    jobTitle: profile?.job_title,
    location: profile?.location,
    phone: profile?.phone,
    website: profile?.website,
    linkedinUrl: profile?.linkedin_url,
    githubUrl: profile?.github_url,
    twitterUrl: profile?.twitter_url,
    userType: profile?.user_type,
    accountStatus: profile?.account_status,
    department: profile?.department,
  };
};